/**
 * AttachmentPicker — file-input button + chip list of attached files.
 *
 * Uploads happen immediately on file select (POST /api/v1/attachments),
 * so by the time the caller submits, we already have persisted
 * attachment IDs to include in the payload. Files hang around on the
 * server until the operator either sends them or deletes them.
 */
import { AlertCircle, Loader2, Paperclip, X } from 'lucide-react';
import { useRef, useState } from 'react';

import { attachmentsApi, type AttachmentRow } from '@/api/attachments';

interface Props {
  attached: AttachmentRow[];
  onChange: (next: AttachmentRow[]) => void;
  /** Prevent additional uploads while a parent form is submitting. */
  disabled?: boolean;
}

export function AttachmentPicker({ attached, onChange, disabled = false }: Props): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    setError(null);
    setUploading(true);
    const uploaded: AttachmentRow[] = [];
    for (const file of Array.from(files)) {
      try {
        const row = await attachmentsApi.upload(file);
        uploaded.push(row);
      } catch (err) {
        setError(err instanceof Error ? err.message : `Upload failed for ${file.name}`);
        break;
      }
    }
    if (uploaded.length > 0) onChange([...attached, ...uploaded]);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function remove(id: string): Promise<void> {
    // Delete from the server too — no orphan files sitting on disk.
    try {
      await attachmentsApi.remove(id);
    } catch {
      /* best-effort — if the delete fails, we still drop it from the list */
    }
    onChange(attached.filter((a) => a.id !== id));
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
          className="inline-flex items-center gap-1.5 rounded-md border border-mb-border bg-mb-surface px-2.5 py-1 text-xs text-mb-text hover:bg-mb-bg-soft disabled:opacity-50"
          title="Attach a file (up to 15 MB per file)"
        >
          {uploading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Paperclip className="h-3.5 w-3.5" />
          )}
          {uploading ? 'Uploading…' : 'Attach file'}
        </button>

        {attached.map((a) => (
          <span
            key={a.id}
            className="inline-flex items-center gap-1.5 rounded-md border border-mb-accent/40 bg-mb-accent-soft px-2 py-1 text-[11px] text-mb-accent-press"
          >
            <Paperclip className="h-3 w-3" />
            <span className="max-w-[14rem] truncate">{a.filename}</span>
            <span className="text-mb-text-subtle">({formatBytes(a.size_bytes)})</span>
            <button
              type="button"
              onClick={() => void remove(a.id)}
              disabled={disabled}
              className="text-mb-text-muted hover:text-mb-danger disabled:opacity-40"
              aria-label={`Remove ${a.filename}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        onChange={(e) => void handleFiles(e.target.files)}
        className="hidden"
      />
      {error ? (
        <div role="alert" className="flex items-start gap-1.5 rounded-md border border-mb-danger/30 bg-mb-danger-soft p-2 text-xs text-mb-danger">
          <AlertCircle className="h-3.5 w-3.5 mt-px shrink-0" />
          {error}
        </div>
      ) : null}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
